import {
    ComponentProps,
    MouseEvent,
    useEffect,
    useState,
} from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryKeyword } from 'src/components/Keywords/hooks/useQueryKeyword';
import { KeywordDetailsForm } from 'src/components/Keywords/KeywordDetailsDrawer/KeywordDetailsForm';
import { useDeleteKeyword } from 'src/components/Keywords/hooks/useDeleteKeyword';
import { useMutateKeyword } from 'src/components/Keywords/hooks/useMutateKeyword';
import { DetailsDrawer } from 'src/components/common/DetailsDrawer';
import { FormArea } from 'src/components/common/DetailsDrawer/FormArea';
import { drawerUpdatedState } from 'src/atoms/drawerUpdatedAtom';
import { useCreateKeyword } from 'src/components/Keywords/hooks/useCreateKeyword';

export interface PropTypes extends Omit<ComponentProps<typeof DetailsDrawer>, 'id' | 'onDelete' | 'onSave'> {
    id?: string;
}

export const KeywordDetailsDrawer = (props: PropTypes): JSX.Element => {
    const {
        id = '',
        open,
        onClose,
        ...rest
    } = props;
    const accessToken = useRecoilValue(isAuthorizedQuery) as string;
    const [keywordName, setKeywordName] = useState<string>('');
    const [isExcludedFromAltText, setIsExcludedFromAltText] = useState<boolean>(false);
    const setUpdatedState = useSetRecoilState(drawerUpdatedState);

    const {
        data,
    } = useQueryKeyword(accessToken, id, {
        enabled: !!id,
        suspense: false,
        refetchInterval: false,
    });

    const createMutation = useCreateKeyword();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const mutation = id && useMutateKeyword(accessToken, id);
    const deleteMutation = useDeleteKeyword();

    useEffect(() => {
        if (data) {
            setKeywordName(data.keywordName);
            setIsExcludedFromAltText(data.isExcludedFromAltText);
        }
    }, [data]);

    const handleClose = (event: MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (onClose) {
            onClose(event, 'escapeKeyDown');
        }
    };

    const handleDelete = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        if (id) {
            await deleteMutation.mutateAsync({ accessToken, id });
        }

        handleClose(event);
    };

    const handleKeywordNameChange = (newKeywordName: string): void => {
        setKeywordName(newKeywordName);
        setUpdatedState(true);
    };

    const handleIsExcludedFromAltTextChange = (newIsExcludedFromAltText: boolean): void => {
        setIsExcludedFromAltText(newIsExcludedFromAltText);
        setUpdatedState(true);
    };

    const handleSave = async (): Promise<void> => {
        if (id && mutation) {
            const updatedKeyword: Models.ContentStoreApi.V3.UpdateKeyword = {
                keywordName,
                isExcludedFromAltText,
            };

            await mutation.mutateAsync({ accessToken, id, keyword: updatedKeyword });
        } else {
            await createMutation.mutateAsync({
                accessToken,
                keyword: {
                    keywordName,
                },
            });
        }
        setUpdatedState(false);
    };

    return (
        <DetailsDrawer
            open={open}
            onClose={onClose}
            onDelete={handleDelete}
            onSave={handleSave}
            {...rest}
        >
            <FormArea title="Keyword Details">
                <KeywordDetailsForm
                    id={id}
                    isExcludedFromAltText={isExcludedFromAltText}
                    keywordName={keywordName}
                    onIsExcludedFromAltTextChange={handleIsExcludedFromAltTextChange}
                    onKeywordNameChange={handleKeywordNameChange}
                />
            </FormArea>
        </DetailsDrawer>
    );
};
